import { createReducer, createActions } from "reduxsauce";
import { DateTime } from "luxon";
import { TYPES_OF_LISTING } from "themes/cashrent/pages/listing/types";

/**
 * ------------- Types and Action Creators -------------
 * The keys of the object will become keys of the Creators.
 * They will also become the keys of the Types after being
 * converted to SCREAMING_SNAKE_CASE.
 */
const { Types, Creators } = createActions({
  createListing: ["data"],
  createListingFailure: null,
  createListingSuccess: null,
  fetchListingDetails: ["id"],
  fetchListingDetailsFailure: null,
  fetchListingDetailsSuccess: ["data"],
  setListingData: ["data"],
  updateListingDetails: ["id", "data", "callback"],
  fetchSoilAreas: ["listing"],
  fetchSoilAreasSuccess: ["data"],
  fetchSoilAreasFailure: null,
  updateListingHuntingInsurance: ["id", "data"],
  updateListingDetailsFailure: null,
  updateListingDetailsSuccess: ["data"],
  updateListingPaymentSchedule: ["data"],
  updateListingHuntingInsuranceSuccess: ["data"],
  updateListingFarmLocation: ["location"],
  publishListing: ["id", "data"],
  publishListingFailure: null,
  publishListingSuccess: ["status"],
  pendingListingSuccess: ["status"],
  clearListingDetails: null,
  uploadFileSuccess: ["file"],
  removeFile: ["id", "listing_id"],
  removeFileFailure: null,
  removeFileSuccess: ["id"],
  soldListing: ["id", "data"],
  soldListingFailure: null,
  soldListingSuccess: null,
});

export const ListingTypes = Types;
export default Creators;

/* ------------- Initial State ------------- */
const now = DateTime.now();

export const INITIAL_STATE = {
  start_date: now.toISO(),
  lease_start_date: now.plus({ months: 2 }).toISO(),
  bidding_end: now.plus({ months: 2, days: 7 }).toISO(),
  type: TYPES_OF_LISTING.AGRICULTURAL,
  isAgricultural: true,
  timeZone: "America/Chicago",
  contract_language_id: null,
  farm: {
    aerialMap: {},
    soilMap: {},
    lng: null,
    lat: null,
    address: null,
    zipCode: null,
    name: null,
    owner: {
      id: null,
    },
    city: {
      id: null,
      name: null,
      state: {
        id: null,
        name: null,
      },
    },
  },
  user: {
    id: null,
  },
  insurance: {
    docksOrPiersCount: 0,
    boatsCanDockCount: 0,
    lakesOrPondsCount: 0,
    boatsForHuntersCount: 0,
    accomodationCount: 0,
    farming: false,
    huntingPermitAllowed: false,
  },
  ground_options: [],
  listingFiles: [],
  isFetching: true,
  isUpdating: false,
  isPublishing: false,
  isUploading: false,
  isRemoving: false,
  isFetchingSoilAreas: false,
  isInitialState: true,
  isCreatingListing: false,
  paymentsSchedule: [],
  isMarkingListingAsSold: false,
  isSold: false,
};

/* ------------- Selectors ------------- */

export const ListingSelectors = {
  listingData: (state) => ({
    ...state.listing, // TODO: Remove this and clear out things that we don't need
    id: state.listing.id,
    title: state.listing.title,
    ground_options: state.listing.ground_options,
    insurance: state.listing.insurance,
    farmId: state.listing.farm.id,
    stateId: state.listing.farm.city.state.id,
    cityId: state.listing.farm.city.id,
    state: state.listing.farm.city.state.name,
    address: state.listing.farm.address,
    city: state.listing.farm.city.name,
    county: state.listing.farm.city.county,
    fsa_map_data: state.listing.farm.aerialMap,
    tillable_map_data: state.listing.farm.soilMap,
    owner_id: state.listing.farm.owner.id,
    lng: state.listing.farm.lng,
    lat: state.listing.farm.lat,
    priceType: state.listing.price_type,
    aerialMap: state.listing.farm.aerialMap,
    soilMap: state.listing.farm.soilMap,
    transactionType: state.listing.transactionType,
    status: state.listing.status,
    paymentsSchedule: state.listing.paymentsSchedule,
  }),
};

/* ------------- Reducers ------------- */

export const createListing = (state = INITIAL_STATE) => ({
  ...state,
  isCreatingListing: true,
});

export const createListingFailure = (state = INITIAL_STATE) => ({
  ...state,
  isCreatingListing: false,
});

export const createListingSuccess = () => INITIAL_STATE;

export const fetchListingDetails = (state = INITIAL_STATE) => ({
  ...state,
  isFetching: true,
});

export const updateListingPaymentSchedule = (
  state = INITIAL_STATE,
  action,
) => ({
  ...state,
  paymentsSchedule: action.data.paymentsSchedule,
});

export const fetchListingDetailsFailure = (state = INITIAL_STATE) => ({
  ...state,
  isFetching: false,
});

export const fetchListingDetailsSuccess = (state = INITIAL_STATE, action) => {
  const { data } = action;

  return {
    ...state,
    ...data,
    isFetching: false,
  };
};

export const setListingData = (state = INITIAL_STATE, action) => ({
  ...state,
  ...action.data,
});

export const fetchSoilAreas = (state = INITIAL_STATE, action) => ({
  ...state,
  ...action.data,
  isFetchingSoilAreas: true,
});

export const fetchSoilAreasSuccess = (state = INITIAL_STATE, action) => ({
  ...state,
  average_estimate_type: action.data.average_estimate_type,
  average_estimate_index: action.data.average_estimate_index,
  isFetchingSoilAreas: false,
});

export const fetchSoilAreasFailure = (state = INITIAL_STATE) => ({
  ...state,
  isFetchingSoilAreas: false,
});

export const updateListingDetails = (state = INITIAL_STATE) => ({
  ...state,
  isUpdating: true,
});

export const updateListingHuntingInsurance = (state = INITIAL_STATE) => ({
  ...state,
  isUpdating: true,
});

export const updateListingDetailsFailure = (state = INITIAL_STATE) => ({
  ...state,
  isUpdating: false,
});

export const updateListingDetailsSuccess = (state = INITIAL_STATE, action) => ({
  ...state,
  // Updates information needed for the payment terms section
  lease_price: action.data.lease_price,
  lease_terms: action.data.lease_terms,
  lease_start_date: action.data.lease_start_date,
  start_date: action.data.start_date,
  bidding_end: action.data.bidding_end,
  duration: action.data.lease_terms,
  isUpdating: false,
  ground_options: action.data.ground_options,
  total_hunter: action.data.total_hunter,
  legal_description: action.data.legal_description,
  contract_terms: action.data.contract_terms,
  contract_language_id: action.data.contract_language_id || null,
  description: action.data.description,
  price_type: action.data.price_type,
  paymentsSchedule: action.data.paymentsSchedule,
});

export const updateListingHuntingInsuranceSuccess = (
  state = INITIAL_STATE,
  action,
) => ({
  ...state,
  insurance: {
    ...state.insurance,
    docksOrPiersCount: action.data.docks_or_piers_count || 0,
    boatsCanDockCount: action.data.boats_can_dock_count || 0,
    lakesOrPondsCount: action.data.lakes_or_ponds_count || 0,
    boatsForHuntersCount: action.data.boats_for_hunters_count || 0,
    accomodationCount: action.data.accomodation_count || 0,
    farming: action.data.has_farming === "yes",
    huntingPermitAllowed: action.data.is_hunting_permit_allowed === "yes",
  },
});

export const publishListing = (state = INITIAL_STATE) => ({
  ...state,
  isPublishing: true,
});

export const publishListingFailure = (state = INITIAL_STATE) => ({
  ...state,
  isPublishing: false,
});

export const publishListingSuccess = (state = INITIAL_STATE, action) => {
  const { status } = action;

  return {
    ...state,
    isDraft: !state.isDraft,
    isActive: !state.isActive,
    isPublishing: false,
    status,
  };
};

export const pendingListingSuccess = (state = INITIAL_STATE, action) => {
  const { status } = action;

  return {
    ...state,
    isDraft: !state.isDraft,
    isPublishing: false,
    isPendingConfirmation: true,
    status,
  };
};

export const updateListingFarmLocation = (state = INITIAL_STATE, action) => {
  const { location } = action;

  return {
    ...state,
    farm: {
      ...state.farm,
      lat: location.lat,
      lng: location.lng,
    },
    state: location.stateId,
    city: location.cityId,
    isUpdating: false,
  };
};

export const clearListingDetails = () => INITIAL_STATE;

export const uploadFileSuccess = (state = INITIAL_STATE, action) => {
  const { file } = action;
  return {
    ...state,
    isUploading: false,
    listingFiles: [...state.listingFiles, file],
  };
};

export const removeFile = (state = INITIAL_STATE) => ({
  ...state,
  isRemoving: true,
});

export const removeFileSuccess = (state = INITIAL_STATE, action) => {
  const { id } = action;

  return {
    ...state,
    isRemoving: false,
    listingFiles: [...state.listingFiles.filter((file) => file.id !== id)],
  };
};

export const removeFileFailure = (state = INITIAL_STATE) => ({
  ...state,
  isRemoving: false,
});

export const soldListing = (state = INITIAL_STATE) => ({
  ...state,
  isMarkingListingAsSold: true,
});

export const soldListingFailure = (state = INITIAL_STATE) => ({
  ...state,
  isMarkingListingAsSold: false,
});

export const soldListingSuccess = (state = INITIAL_STATE) => ({
  ...state,
  isSold: !state.isSold,
  isMarkingListingAsSold: false,
});

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.CREATE_LISTING]: createListing,
  [Types.CREATE_LISTING_FAILURE]: createListingFailure,
  [Types.CREATE_LISTING_SUCCESS]: createListingSuccess,
  [Types.FETCH_LISTING_DETAILS]: fetchListingDetails,
  [Types.UPDATE_LISTING_PAYMENT_SCHEDULE]: updateListingPaymentSchedule,
  [Types.FETCH_LISTING_DETAILS_FAILURE]: fetchListingDetailsFailure,
  [Types.FETCH_LISTING_DETAILS_SUCCESS]: fetchListingDetailsSuccess,
  [Types.SET_LISTING_DATA]: setListingData,
  [Types.FETCH_SOIL_AREAS]: fetchSoilAreas,
  [Types.FETCH_SOIL_AREAS_SUCCESS]: fetchSoilAreasSuccess,
  [Types.FETCH_SOIL_AREAS_FAILURE]: fetchSoilAreasFailure,
  [Types.UPDATE_LISTING_DETAILS]: updateListingDetails,
  [Types.UPDATE_LISTING_HUNTING_INSURANCE]: updateListingHuntingInsurance,
  [Types.UPDATE_LISTING_DETAILS_FAILURE]: updateListingDetailsFailure,
  [Types.UPDATE_LISTING_DETAILS_SUCCESS]: updateListingDetailsSuccess,
  [Types.UPDATE_LISTING_HUNTING_INSURANCE_SUCCESS]:
    updateListingHuntingInsuranceSuccess,
  [Types.PUBLISH_LISTING]: publishListing,
  [Types.PUBLISH_LISTING_FAILURE]: publishListingFailure,
  [Types.PUBLISH_LISTING_SUCCESS]: publishListingSuccess,
  [Types.PENDING_LISTING_SUCCESS]: pendingListingSuccess,
  [Types.UPDATE_LISTING_FARM_LOCATION]: updateListingFarmLocation,
  [Types.CLEAR_LISTING_DETAILS]: clearListingDetails,
  [Types.UPLOAD_FILE_SUCCESS]: uploadFileSuccess,
  [Types.REMOVE_FILE]: removeFile,
  [Types.REMOVE_FILE_FAILURE]: removeFileFailure,
  [Types.REMOVE_FILE_SUCCESS]: removeFileSuccess,
  [Types.SOLD_LISTING]: soldListing,
  [Types.SOLD_LISTING_FAILURE]: soldListingFailure,
  [Types.SOLD_LISTING_SUCCESS]: soldListingSuccess,
});
